import React from 'react';
import { Grid, Paper, Table, TableBody, TableCell, TableRow, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { LocationPosition, LocationStable } from '../../models/locations';
import { AnimalType } from '../../models/animal';
import { HealthSystemDomainLocationsResultLocationViewModel } from '../../api/HealthSystemApi';
import useFeatures, { FeatureType } from '../../hooks/features';
import { useFarmDateTime } from '../../hooks/useFarmDateTime';
import { useStyles } from './styles';

export interface LocationDetailsProps {
  location: HealthSystemDomainLocationsResultLocationViewModel;
  /**
   * `full` is the thirteen-row table the Location overview has always shown. `compact` is a one-line strip for
   * the tabs that only need to say which pen is on screen - it must stay short enough to keep those tabs' own
   * filters above the fold.
   */
  variant?: 'full' | 'compact';
}

/**
 * The identity of a single pen, rendered from the `location` the page already holds - every tab that shows it
 * reads the same object, so none of them costs an extra call.
 */
const LocationDetails: React.FC<LocationDetailsProps> = ({ location, variant = 'full' }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { hasFeature } = useFeatures();
  const { formatDate, formatTime } = useFarmDateTime();

  const getAreaComplianceClass = (isAreaCompliant: boolean): string => {
    if (!hasFeature(FeatureType.USE_WELFARE_AREA_CHECKING)) return '';
    return isAreaCompliant === false ? classes.marked : '';
  };

  const renderSquareMetersPerPig = (): string => {
    const area = location.area?.areaInM ?? 0;
    const count = location.animalsCount ?? 0;

    if (count <= 0 || area <= 0) {
      return '-';
    }

    const sqmPerPig = area / count;
    return sqmPerPig.toFixed(2);
  };

  if (variant === 'compact') {
    return <LocationSummaryStrip location={location} />;
  }

  return (
    <Table size="small" aria-label="location data table" id="location-data-table">
      <TableBody>
        <TableRow key="locationNumber">
          <TableCell align="right">{t('f-chip-number')}</TableCell>
          <TableCell className={classes.bold}>{location.number}</TableCell>
        </TableRow>

        <TableRow key="locationRoom">
          <TableCell align="right">{t('f-loc-room')}</TableCell>
          <TableCell className={classes.bold}>{location.room}</TableCell>
        </TableRow>

        <TableRow key="locationPen">
          <TableCell align="right">{t('f-loc-pen')}</TableCell>
          <TableCell className={classes.bold}>{location.pen}</TableCell>
        </TableRow>

        <TableRow key="locationArea">
          <TableCell align="right">{t('f-loc-area')}</TableCell>
          <TableCell className={classes.bold}>
            {t('f-loc-area-value', { value: location.area?.areaInM || 0 })}
          </TableCell>
        </TableRow>

        <TableRow key="squareMetersPerPig" className={getAreaComplianceClass(location.isAreaCompliant)}>
          <TableCell align="right">{t('f-square-meters-per-pig')}</TableCell>
          <TableCell className={classes.bold}> {renderSquareMetersPerPig()}</TableCell>
        </TableRow>

        <TableRow key="locationPosition">
          <TableCell align="right">{t('f-loc-pos')}</TableCell>
          <TableCell className={classes.bold}>{LocationPosition[location.positioning]}</TableCell>
        </TableRow>

        <TableRow key="locationStable">
          <TableCell align="right">{t('f-loc-stable')}</TableCell>
          <TableCell className={classes.bold}>{LocationStable[location.stable]}</TableCell>
        </TableRow>

        <TableRow key="animalType">
          <TableCell align="right">{t('f-com-animal-type')}</TableCell>
          <TableCell className={classes.bold}>{AnimalType[location.animalType]}</TableCell>
        </TableRow>

        {/* Replaces the old climate provider / device number rows. Those read
            `metadata`, which nothing writes any more, so they showed a stale number
            or nothing at all. This is the device actually bound to the pen, named the
            way it reads at the provider, with the last time it was heard from - which
            is what tells a reader whether the climate tab has current data. */}
        <TableRow key="sensorDevice">
          <TableCell align="right">{t('f-loc-view-sensor-device')}</TableCell>
          <TableCell className={classes.bold}>
            {/* `f-com-none`, not `f-sensor-dev-unbound` ("No pen"): that wording
                belongs in the table's "Bound to" column, where the missing thing is
                the pen. Here the row is labelled "Sensor device", so the missing
                thing is the device. */}
            {location.sensorDevice?.name || t('f-com-none')}
          </TableCell>
        </TableRow>

        {location.sensorDevice?.lastSeenAt && (
          <TableRow key="sensorDeviceLastSeen">
            <TableCell align="right">{t('f-loc-view-sensor-device-seen')}</TableCell>
            <TableCell className={classes.bold}>
              {`${formatDate(location.sensorDevice.lastSeenAt)} ${formatTime(location.sensorDevice.lastSeenAt)}`}
            </TableCell>
          </TableRow>
        )}

        {location.cycleStartDate && (
          <>
            <TableRow key="locationCycleStartDate">
              <TableCell align="right">{t('f-loc-view-cycle')}</TableCell>
              <TableCell className={classes.bold}>{new Date(location.cycleStartDate).toLocaleDateString()}</TableCell>
            </TableRow>

            <TableRow key="avgAge">
              <TableCell align="right">{t('f-loc-view-avg-age')}</TableCell>
              <TableCell className={classes.bold}>{t('f-com-age-days', { age: location.averageAge })}</TableCell>
            </TableRow>
          </>
        )}

        <TableRow key="animalsCount">
          <TableCell align="right">{t('f-loc-cyc-animal-amount')}</TableCell>
          <TableCell className={classes.bold}>{`${location.animalsCount || '-'}`}</TableCell>
        </TableRow>
      </TableBody>
    </Table>
  );
};

interface SummaryEntry {
  key: string;
  label: string;
  value: string;
}

const LocationSummaryStrip: React.FC<{ location: HealthSystemDomainLocationsResultLocationViewModel }> = ({
  location
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  // `LocationStable[0]` and `AnimalType[0]` are both the string "Unknown", and `positioning` has no member for
  // the unset value at all. An unset pen would read as a strip of "Unknown"s, so those entries are dropped
  // instead. The full table keeps showing them - there the label column makes it clear what is missing.
  const stable = location.stable ? LocationStable[location.stable] : null;
  const animalType = location.animalType ? AnimalType[location.animalType] : null;
  const position = LocationPosition[location.positioning] ?? null;
  const roomAndPen = [location.room, location.pen].filter(Boolean).join(' / ');

  const entries: SummaryEntry[] = [
    { key: 'number', label: t('f-chip-number'), value: location.number ?? '' },
    { key: 'stable', label: t('f-loc-stable'), value: stable ?? '' },
    { key: 'roomAndPen', label: `${t('f-loc-room')} / ${t('f-loc-pen')}`, value: roomAndPen },
    { key: 'position', label: t('f-loc-pos'), value: position ?? '' },
    { key: 'animalType', label: t('f-com-animal-type'), value: animalType ?? '' },
    { key: 'animalsCount', label: t('f-loc-cyc-animal-amount'), value: `${location.animalsCount || '-'}` }
  ].filter((entry) => entry.value !== '');

  return (
    <Paper className={classes.summary} elevation={1}>
      <Grid container direction="row" alignItems="center" id="location-summary-strip">
        {entries.map((entry) => (
          <Grid item key={entry.key} className={classes.summaryItem}>
            <Typography variant="caption" className={classes.summaryLabel}>
              {entry.label}
            </Typography>
            <Typography variant="body2" className={classes.summaryValue}>
              {entry.value}
            </Typography>
          </Grid>
        ))}
      </Grid>
    </Paper>
  );
};

export default LocationDetails;
